import { useState, useEffect } from "react";

// Storage key for user's last seen version
const VERSION_STORAGE_KEY = "version";

// Get the app version from environment or package.json
const getAppVersion = () => {
  return process.env.REACT_APP_VERSION || require("../../package.json").version;
};

// Parse semantic version string into components
const parseVersion = (version) => {
  const [major = 0, minor = 0, patch = 0] = version.split(".").map(Number);
  return { major, minor, patch, full: version };
};

// Compare two semantic versions
const compareVersions = (v1, v2) => {
  const version1 = parseVersion(v1);
  const version2 = parseVersion(v2);

  if (version1.major !== version2.major) {
    return version1.major > version2.major ? 1 : -1;
  }
  if (version1.minor !== version2.minor) {
    return version1.minor > version2.minor ? 1 : -1;
  }
  if (version1.patch !== version2.patch) {
    return version1.patch > version2.patch ? 1 : -1;
  }
  return 0;
};

// Check if there's a major or minor version change
const hasMajorOrMinorChange = (oldVersion, newVersion) => {
  if (!oldVersion) return false; // No previous version = no update to show

  const v1 = parseVersion(oldVersion);
  const v2 = parseVersion(newVersion);

  return v1.major !== v2.major || v1.minor !== v2.minor;
};

/**
 * Hook to manage application version information
 *
 * @returns {Object} Version information and utilities
 * @returns {string} appVersion - Current application version
 * @returns {string|null} userVersion - User's last seen version
 * @returns {Object} parsedAppVersion - Parsed app version {major, minor, patch, full}
 * @returns {Object|null} parsedUserVersion - Parsed user version {major, minor, patch, full}
 * @returns {boolean} hasNewVersion - Whether there's any version change
 * @returns {boolean} hasSignificantUpdate - Whether there's a major/minor version change
 * @returns {Function} updateUserVersion - Function to update user's version
 * @returns {Function} parseVersion - Utility to parse version strings
 * @returns {Function} compareVersions - Utility to compare versions
 */
export const useVersion = () => {
  const [appVersion] = useState(getAppVersion);
  const [userVersion, setUserVersion] = useState(() => {
    return localStorage.getItem(VERSION_STORAGE_KEY);
  });

  // Update user version in localStorage and state
  const updateUserVersion = (version = appVersion) => {
    localStorage.setItem(VERSION_STORAGE_KEY, version);
    setUserVersion(version);
  };

  // Check if versions are different
  const hasNewVersion = userVersion !== appVersion;
  const hasSignificantUpdate = hasMajorOrMinorChange(userVersion, appVersion);

  // Parse versions for detailed comparison
  const parsedAppVersion = parseVersion(appVersion);
  const parsedUserVersion = userVersion ? parseVersion(userVersion) : null;

  // Auto-update stored version if different (but don't trigger modal)
  useEffect(() => {
    if (hasNewVersion) {
      updateUserVersion(appVersion);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appVersion, hasNewVersion]);

  return {
    appVersion,
    userVersion,
    parsedAppVersion,
    parsedUserVersion,
    hasNewVersion,
    hasSignificantUpdate,
    updateUserVersion,
    // Expose utilities
    parseVersion,
    compareVersions,
  };
};

// Export utilities separately for use outside of React components
export {
  parseVersion,
  compareVersions,
  hasMajorOrMinorChange,
  VERSION_STORAGE_KEY,
};
